import React from 'react';
import { Trash2, Plus, GripVertical, ChevronDown, Copy } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useERDStore } from './erdStore';
import {
  NODE_TYPE_CONFIG, RELATIONSHIP_CONFIG,
  NODE_TYPES, RELATIONSHIP_TYPES, FIELD_TYPES,
} from './erdConstants';

function Select({ value, onChange, options, getLabel }) {
  return (
    <div className="erd-select-wrap">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="erd-select"
      >
        {options.map(o => <option key={o} value={o}>{getLabel(o)}</option>)}
      </select>
      <ChevronDown size={12} className="erd-select-chevron" />
    </div>
  );
}

function Label({ children }) {
  return <p className="erd-label">{children}</p>;
}

function Section({ title, children }) {
  return (
    <div className="erd-section">
      <p className="erd-section-title">{title}</p>
      {children}
    </div>
  );
}

function NodePanel() {
  const { selectedNode, updateNodeData, updateNodeType, deleteNode, duplicateNode } = useERDStore();
  const node = selectedNode();
  if (!node) return null;
  const nodeId = node.id;
  const data = node.data;
  const nodeType = node.type ?? 'collection';
  const fields = data.fields ?? [];
  const subcollections = data.subcollections ?? [];

  function updateField(fieldId, update) {
    const newFields = fields.map(f => f.id === fieldId ? { ...f, ...update } : f);
    updateNodeData(nodeId, { fields: newFields });
  }
  function addField() {
    updateNodeData(nodeId, { fields: [...fields, { id: nanoid(), name: 'new_field', type: 'string' }] });
  }
  function removeField(fieldId) {
    updateNodeData(nodeId, { fields: fields.filter(f => f.id !== fieldId) });
  }
  function addSubcollection() {
    updateNodeData(nodeId, { subcollections: [...subcollections, 'new_subcollection'] });
  }
  function updateSubcollection(i, val) {
    const next = [...subcollections]; next[i] = val;
    updateNodeData(nodeId, { subcollections: next });
  }
  function removeSubcollection(i) {
    updateNodeData(nodeId, { subcollections: subcollections.filter((_, j) => j !== i) });
  }

  const cfg = NODE_TYPE_CONFIG[nodeType];

  return (
    <div className="erd-panel-content">
      <div className="erd-panel-pill" style={{ backgroundColor: cfg.headerBg + '44', color: cfg.textColor, border: `1px solid ${cfg.border}44` }}>
        {cfg.label}
      </div>

      <Label>Name</Label>
      <input value={data.label} onChange={e => updateNodeData(nodeId, { label: e.target.value })} className="erd-field-input erd-mono" placeholder="collection_name" />

      <Label>Node Type</Label>
      <div className="erd-mb">
        <Select value={nodeType} onChange={t => updateNodeType(nodeId, t)} options={NODE_TYPES} getLabel={t => NODE_TYPE_CONFIG[t].label} />
      </div>

      <Label>Description</Label>
      <textarea value={data.description ?? ''} onChange={e => updateNodeData(nodeId, { description: e.target.value })} className="erd-field-input erd-textarea" rows={2} placeholder="What is this collection for?" />

      {nodeType !== 'note' && (
        <>
          <div className="erd-toggle-row">
            <label className="erd-toggle-label">
              <input
                type="checkbox"
                checked={!!data.showFieldHandles}
                onChange={e => updateNodeData(nodeId, { showFieldHandles: e.target.checked })}
              />
              Per-field connection handles
            </label>
          </div>

          <Section title="Fields">
            <div className="erd-fields-list">
              {fields.map(f => (
                <div key={f.id} className="erd-field-row">
                  <div className="erd-field-row-main">
                    <GripVertical size={12} className="erd-grip" />
                    <input value={f.name} onChange={e => updateField(f.id, { name: e.target.value })} className="erd-field-input erd-field-name erd-mono" placeholder="field_name" />
                    <button onClick={() => removeField(f.id)} className="erd-field-remove"><Trash2 size={12} /></button>
                  </div>
                  <div className="erd-field-row-meta">
                    <Select value={f.type} onChange={t => updateField(f.id, { type: t })} options={FIELD_TYPES} getLabel={t => t} />
                    <label className="erd-field-req-label">
                      <input type="checkbox" checked={!!f.required} onChange={e => updateField(f.id, { required: e.target.checked })} />
                      req
                    </label>
                  </div>
                  <div className="erd-field-row-desc">
                    <input value={f.description ?? ''} onChange={e => updateField(f.id, { description: e.target.value })} className="erd-field-input erd-field-desc" placeholder="description (optional)" />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addField} className="erd-add-btn"><Plus size={12} /> Add Field</button>
          </Section>

          <Section title="Subcollections">
            <div className="erd-subcollections-list">
              {subcollections.map((sub, i) => (
                <div key={i} className="erd-subcollection-row">
                  <span>📁</span>
                  <input value={sub} onChange={e => updateSubcollection(i, e.target.value)} className="erd-field-input erd-mono" placeholder="subcollection_name" />
                  <button onClick={() => removeSubcollection(i)} className="erd-field-remove"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
            <button onClick={addSubcollection} className="erd-add-btn"><Plus size={12} /> Add Subcollection</button>
          </Section>
        </>
      )}

      <div className="erd-panel-delete-section">
        <button onClick={() => duplicateNode(nodeId)} className="erd-duplicate-btn">
          <Copy size={12} /> Duplicate Node
        </button>
        <button onClick={() => deleteNode(nodeId)} className="erd-delete-btn">
          <Trash2 size={12} /> Delete Node
        </button>
      </div>
    </div>
  );
}

function EdgePanel() {
  const { selectedEdge, updateEdgeData, deleteEdge } = useERDStore();
  const edge = selectedEdge();
  if (!edge) return null;

  const data = edge.data ?? { relationshipType: 'reference', label: '', description: '' };
  const relType = data.relationshipType ?? 'reference';
  const cfg = RELATIONSHIP_CONFIG[relType];

  return (
    <div className="erd-panel-content">
      <div className="erd-panel-pill" style={{ backgroundColor: cfg.color + '22', color: cfg.color, border: `1px solid ${cfg.color}44` }}>
        Edge
      </div>

      <Label>Relationship Type</Label>
      <div className="erd-mb">
        <Select value={relType} onChange={t => updateEdgeData(edge.id, { relationshipType: t })} options={RELATIONSHIP_TYPES} getLabel={t => RELATIONSHIP_CONFIG[t].label} />
      </div>

      <Label>Label</Label>
      <input value={data.label ?? ''} onChange={e => updateEdgeData(edge.id, { label: e.target.value })} className="erd-field-input" placeholder="e.g. activity_id" />

      <Label>Description</Label>
      <textarea value={data.description ?? ''} onChange={e => updateEdgeData(edge.id, { description: e.target.value })} className="erd-field-input erd-textarea" rows={2} placeholder="Notes about this relationship" />

      <div className="erd-panel-delete-section">
        <button onClick={() => deleteEdge(edge.id)} className="erd-delete-btn">
          <Trash2 size={12} /> Delete Edge
        </button>
      </div>
    </div>
  );
}

export default function ERDRightPanel() {
  const { selectedElementType } = useERDStore();

  if (!selectedElementType) {
    return (
      <div className="erd-right-panel erd-right-panel-empty">
        <p>Click a node or edge to edit its properties.</p>
      </div>
    );
  }

  return (
    <div className="erd-right-panel">
      {selectedElementType === 'node' ? <NodePanel /> : <EdgePanel />}
    </div>
  );
}
